<template>
  <div class="min-h-screen bg-bochas-bg px-4 py-6 sm:py-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 class="text-2xl sm:text-3xl font-bold text-white">Asociaciones regionales</h1>
          <p class="text-sm text-gray-400 mt-1">
            {{ filteredAsociaciones.length }} asociaciones cargadas.
          </p>
        </div>
        <div class="flex w-full gap-2 sm:w-auto">
          <router-link
            v-if="canCreateAsociacion"
            to="/admin/asociaciones/nueva"
            class="btn-bochas-rojo w-full text-center text-sm sm:w-auto"
          >
            + Nueva asociación
          </router-link>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <div
          class="inline-block w-10 h-10 sm:w-12 sm:h-12 border-4 border-bochas-rojo border-t-transparent rounded-full animate-spin">
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-bochas-rojo/20 border border-bochas-rojo text-red-200 rounded-lg p-4">
        {{ error }}
      </div>

      <!-- Empty -->
      <div v-if="!loading && !error && asociaciones.length === 0" class="text-center py-12">
        <p class="text-gray-400 mb-4">Aún no hay asociaciones cargadas.</p>
        <router-link to="/admin/asociaciones/nueva" class="btn-bochas-rojo inline-block">
          Crear la primera
        </router-link>
      </div>

      <!-- Tabla desktop / Cards mobile -->
      <div v-else-if="!loading && !error">
        <div class="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 md:grid-cols-4">
          <input
            v-model="search"
            type="search"
            class="rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-sm text-white placeholder-gray-500 outline-none transition-colors focus:border-bochas-rojo"
            placeholder="Buscar asociación"
          />
          <SelectField
            v-model="filters.provinceId"
            :options="provinceFilterOptions"
          />
          <SelectField
            v-model="filters.locality"
            :options="localityFilterOptions"
          />
          <SelectField
            v-model="filters.status"
            :options="statusFilterOptions"
          />
        </div>

        <div
          v-if="canDeleteAsociacion"
          class="mb-3 flex flex-col gap-2 rounded-lg border border-white/10 bg-white/[0.03] p-3 sm:flex-row sm:items-center sm:justify-between"
        >
          <p class="text-sm text-gray-300">
            {{ selectedIds.length }} seleccionadas
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="btn-bochas-trans px-3 py-2 text-xs"
              @click="toggleSelectAll"
            >
              {{ allSelected ? "Quitar selección" : hasActiveFilters ? "Seleccionar filtradas" : "Seleccionar todas" }}
            </button>
            <button
              type="button"
              class="btn-bochas-rojo px-3 py-2 text-xs"
              :disabled="selectedIds.length === 0 || deletingMany"
              :class="{ 'opacity-60 cursor-not-allowed': selectedIds.length === 0 || deletingMany }"
              @click="onDeleteSelected"
            >
              {{ deletingMany ? "Eliminando..." : "Eliminar seleccionadas" }}
            </button>
          </div>
        </div>

        <div class="hidden md:block card-fbochas overflow-hidden">
          <table class="w-full">
            <thead class="bg-white/5 border-b border-white/10">
              <tr class="text-left text-xs uppercase tracking-wide text-gray-400">
                <th v-if="canDeleteAsociacion" class="px-4 py-3">
                  <input
                    type="checkbox"
                    :checked="allSelected"
                    class="h-4 w-4 rounded border-gray-700 bg-bochas-bg text-bochas-rojo focus:ring-bochas-rojo"
                    @change="toggleSelectAll"
                  />
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('id')">
                    ID <span>{{ sortIcon("id") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('name')">
                    Nombre <span>{{ sortIcon("name") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('province')">
                    Provincia <span>{{ sortIcon("province") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('locality')">
                    Localidad <span>{{ sortIcon("locality") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3 text-right">
                  <button type="button" class="table-sort-button ml-auto" @click="setSort('clubs')">
                    Clubes <span>{{ sortIcon("clubs") }}</span>
                  </button>
                </th>
                <th class="px-4 py-3">
                  <button type="button" class="table-sort-button" @click="setSort('status')">
                    Estado <span>{{ sortIcon("status") }}</span>
                  </button>
                </th>
                <th v-if="canViewRecordHistory" class="px-4 py-3">Registro</th>
                <th v-if="canEditAsociacion || canDeleteAsociacion" class="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-white/5">
              <tr v-for="a in filteredAsociaciones" :key="a.id" class="hover:bg-white/5 transition-colors">
                <td v-if="canDeleteAsociacion" class="px-4 py-3">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="a.id"
                    class="h-4 w-4 rounded border-gray-700 bg-bochas-bg text-bochas-rojo focus:ring-bochas-rojo"
                  />
                </td>
                <td class="px-4 py-3 text-sm text-gray-500">#{{ a.id }}</td>
                <td class="px-4 py-3">
                  <div class="text-white font-medium">{{ a.name }}</div>
                  <div v-if="a.president" class="text-xs text-gray-500 mt-0.5">
                    Presidente: {{ a.president }}
                  </div>
                </td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ provinceName(a.provinceId) || "—" }}</td>
                <td class="px-4 py-3 text-sm text-gray-300">{{ a.locality || "—" }}</td>
                <td class="px-4 py-3 text-sm text-gray-300 text-right">{{ clubCount(a) }}</td>
                <td class="px-4 py-3">
                  <span :class="[
                    'text-xs px-2 py-1 rounded',
                    a.status === 'published'
                      ? 'bg-green-500/20 text-green-300'
                      : 'bg-yellow-500/20 text-yellow-300',
                  ]">
                    {{ a.status === 'published' ? 'Publicada' : 'Borrador' }}
                  </span>
                </td>
                <td v-if="canViewRecordHistory" class="px-4 py-3"><AdminRecordDates v-if="canViewRecordHistory" resource="associations" :record="a" /></td>
                <td v-if="canEditAsociacion || canDeleteAsociacion" class="px-4 py-3 text-right">
                  <div class="inline-flex items-center justify-end gap-2">
                    <router-link
                      v-if="canEditAsociacion"
                      :to="`/admin/asociaciones/editar/${a.id}`"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
                      title="Editar"
                      aria-label="Editar asociación"
                    ><IconFbochas name="edit" :size="16" /></router-link>
                    <button
                      v-if="canDeleteAsociacion"
                      @click="onDelete(a)"
                      class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                      title="Eliminar"
                      aria-label="Eliminar asociación"
                    ><IconFbochas name="trash" :size="16" /></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden space-y-3">
          <div v-for="a in filteredAsociaciones" :key="a.id" class="card-fbochas p-4 transition-shadow"
            :class="{ 'ring-1 ring-bochas-rojo': selectedIds.includes(a.id) }">
            <div class="flex items-start justify-between gap-2 mb-2">
              <div class="flex min-w-0 flex-1 items-start gap-2">
                <label v-if="canDeleteAsociacion" class="relative -ml-2 -mt-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="a.id"
                    :aria-label="`Seleccionar ${a.name}`"
                    class="peer h-6 w-6 cursor-pointer appearance-none rounded-md border border-white/30 bg-white/5 transition-colors checked:border-bochas-rojo checked:bg-bochas-rojo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-bochas-bg"
                  />
                  <IconFbochas name="check" :size="16" aria-hidden="true" class="pointer-events-none absolute text-white opacity-0 peer-checked:opacity-100" />
                </label>
                <div class="min-w-0">
                  <p class="text-xs text-gray-500">#{{ a.id }}</p>
                  <h3 class="text-white font-semibold">{{ a.name }}</h3>
                </div>
              </div>
              <span :class="[
                'text-xs px-2 py-1 rounded flex-shrink-0',
                a.status === 'published'
                  ? 'bg-green-500/20 text-green-300'
                  : 'bg-yellow-500/20 text-yellow-300',
              ]">
                {{ a.status === 'published' ? 'Publicada' : 'Borrador' }}
              </span>
            </div>
            <p v-if="a.provinceId || a.locality" class="text-xs text-gray-400 mb-1">
              {{ provinceName(a.provinceId) }}<span v-if="a.provinceId && a.locality"> · </span>{{ a.locality || "" }}
            </p>
            <p class="text-xs text-bochas-text-muted mb-3">
              {{ clubCount(a) }} clubes
            </p>
            <AdminRecordDates v-if="canViewRecordHistory" resource="associations" :record="a" class="mb-3" />
            <div
              v-if="canEditAsociacion || canDeleteAsociacion"
              class="flex items-center justify-end gap-2"
            >
              <router-link
                v-if="canEditAsociacion"
                :to="`/admin/asociaciones/editar/${a.id}`"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-sky-400/25 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20"
               title="Editar" aria-label="Editar"><IconFbochas name="edit" :size="16" /></router-link>
              <button
                v-if="canDeleteAsociacion"
                @click="onDelete(a)"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors border-red-400/25 bg-red-500/10 text-red-300 hover:bg-red-500/20"
               title="Eliminar" aria-label="Eliminar"><IconFbochas name="trash" :size="16" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { isGeneralAdmin } from "@/utils/roles";

import { ref, computed, onMounted } from "vue";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import AdminRecordDates from "@/components/Admin/AdminRecordDates.vue";
import SelectField from "@/components/Common/SelectField.vue";
import { getStoredUser } from "@/services/auth/authService";
import { canManageInstitutional, isAsociacionAdmin } from "@/utils/roles";
import { listarAsociacionesAdmin, eliminarAsociacion } from "@/services/asociaciones/asociacionesService";
import { listarClubesAdmin } from "@/services/clubes/clubesService";
import { obtenerProvinciasAdmin } from "@/services/admin/adminModulesService";
import { registrarInstitucionesResultados } from "@/services/tournaments/tournamentsService";
import { useConfirm } from "@/composables/useConfirm";

const canViewRecordHistory = computed(() => isGeneralAdmin(getStoredUser()));

const user = ref(getStoredUser());
const { confirm } = useConfirm();
const canCreateAsociacion = computed(() => canManageInstitutional(user.value));
const canEditAsociacion = computed(
  () => canManageInstitutional(user.value) || isAsociacionAdmin(user.value),
);
const canDeleteAsociacion = computed(() => canManageInstitutional(user.value));

const asociaciones = ref([]);
const clubes = ref([]);
const provincias = ref([]);
const loading = ref(true);
const error = ref("");
const selectedIds = ref([]);
const deletingMany = ref(false);
const sortBy = ref("name");
const sortDirection = ref("asc");
const search = ref("");
const filters = ref({
  provinceId: "",
  locality: "",
  status: "",
});
const hasActiveFilters = computed(() =>
  Boolean(search.value.trim() || Object.values(filters.value).some(Boolean)),
);
const allSelected = computed(
  () =>
    filteredAsociaciones.value.length > 0 &&
    filteredAsociaciones.value.every((item) => selectedIds.value.includes(item.id)),
);

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function provinceName(id) {
  if (!id) return "";
  return provincias.value.find((item) => String(item.id) === String(id))?.name || "Provincia no disponible";
}

const localityOptions = computed(() => {
  const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
  return [...new Set(asociaciones.value.map((item) => item.locality).filter(Boolean))].sort(collator.compare);
});
const provinceFilterOptions = computed(() => [
  { value: "", label: "Todas las provincias" },
  ...provincias.value.slice()
    .sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }))
    .map((province) => ({ value: province.id, label: province.name })),
]);
const localityFilterOptions = computed(() => [
  { value: "", label: "Todas las localidades" },
  ...localityOptions.value.map((locality) => ({ value: locality, label: locality })),
]);
const statusFilterOptions = [
  { value: "", label: "Todos los estados" },
  { value: "published", label: "Publicada" },
  { value: "draft", label: "Borrador" },
];

const filteredAsociaciones = computed(() => {
  const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });
  const term = normalizeText(search.value);

  return asociaciones.value
    .filter((item) => {
      const matchesSearch = !term || normalizeText([
        item.id,
        item.name,
        item.president,
        provinceName(item.provinceId),
        item.locality,
        item.status,
      ].join(" ")).includes(term);

      return (
        matchesSearch &&
        (!filters.value.provinceId || String(item.provinceId || "") === filters.value.provinceId) &&
        (!filters.value.locality || item.locality === filters.value.locality) &&
        (!filters.value.status || item.status === filters.value.status)
      );
    })
    .sort((a, b) => {
      let result = 0;
      if (sortBy.value === "id") result = a.id - b.id;
      else if (sortBy.value === "clubs") result = clubCount(a) - clubCount(b);
      else if (sortBy.value === "province") result = collator.compare(provinceName(a.provinceId), provinceName(b.provinceId));
      else {
        const key = sortBy.value;
        result = collator.compare(String(a[key] || ""), String(b[key] || ""));
      }
      return sortDirection.value === "asc" ? result : -result;
    });
});

async function load() {
  loading.value = true;
  error.value = "";
  try {
    if (canManageInstitutional(user.value)) await registrarInstitucionesResultados();
    const [data, clubesData, provincesData] = await Promise.all([
      listarAsociacionesAdmin(),
      listarClubesAdmin(),
      obtenerProvinciasAdmin(),
    ]);
    asociaciones.value = data.items || [];
    clubes.value = clubesData.items || [];
    provincias.value = provincesData;
    selectedIds.value = selectedIds.value.filter((id) =>
      asociaciones.value.some((item) => item.id === id),
    );
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "Error al cargar.";
  } finally {
    loading.value = false;
  }
}

function clubCount(asociacion) {
  const count = clubes.value.filter(
    (club) => Number(club.asociacionId) === Number(asociacion.id),
  ).length;
  return count || asociacion.clubCount || 0;
}

function toggleSelectAll() {
  const filteredIds = filteredAsociaciones.value.map((a) => a.id);
  selectedIds.value = allSelected.value
    ? selectedIds.value.filter((id) => !filteredIds.includes(id))
    : [...new Set([...selectedIds.value, ...filteredIds])];
}

function setSort(key) {
  if (sortBy.value === key) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
    return;
  }
  sortBy.value = key;
  sortDirection.value = "asc";
}

function sortIcon(key) {
  if (sortBy.value !== key) return "↕";
  return sortDirection.value === "asc" ? "↑" : "↓";
}

async function onDelete(a) {
  const confirmed = await confirm({
    title: "Eliminar asociación",
    message: `¿Eliminar la asociación "${a.name}"? Está acción no se puede deshacer.`,
  });
  if (!confirmed) return;
  try {
    await eliminarAsociacion(a.id);
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "No se pudo eliminar.";
  }
}

async function onDeleteSelected() {
  if (!selectedIds.value.length) return;
  const confirmed = await confirm({
    title: "Eliminar asociaciones",
    message: `¿Eliminar ${selectedIds.value.length} asociaciones seleccionadas? Está acción no se puede deshacer.`,
  });
  if (!confirmed) return;
  deletingMany.value = true;
  try {
    await Promise.all(selectedIds.value.map((id) => eliminarAsociacion(id)));
    selectedIds.value = [];
    await load();
  } catch (err) {
    error.value = err.response?.data?.message || err.message || "No se pudieron eliminar las asociaciones.";
  } finally {
    deletingMany.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.table-sort-button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
  transition: color 160ms ease;
}

.table-sort-button:hover {
  color: white;
}
</style>
